/**
 * File utility functions for file validation, formatting, and type detection
 */

// File type limits (in bytes)
export const FILE_LIMITS = {
  image: {
    maxSize: 10 * 1024 * 1024, // 10MB
    extensions: ["jpg", "jpeg", "png", "gif", "webp", "svg"],
    mimeTypes: [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
      "image/svg+xml",
    ],
  },
  pdf: {
    maxSize: 50 * 1024 * 1024, // 50MB
    extensions: ["pdf"],
    mimeTypes: ["application/pdf"],
  },
  document: {
    maxSize: 20 * 1024 * 1024, // 20MB
    extensions: ["doc", "docx", "txt", "rtf", "odt"],
    mimeTypes: [
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
      "application/rtf",
      "application/vnd.oasis.opendocument.text",
    ],
  },
  presentation: {
    maxSize: 50 * 1024 * 1024, // 50MB
    extensions: ["ppt", "pptx", "odp"],
    mimeTypes: [
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "application/vnd.oasis.opendocument.presentation",
    ],
  },
  spreadsheet: {
    maxSize: 20 * 1024 * 1024, // 20MB
    extensions: ["xls", "xlsx", "csv", "ods"],
    mimeTypes: [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
      "application/vnd.oasis.opendocument.spreadsheet",
    ],
  },
  code: {
    maxSize: 5 * 1024 * 1024, // 5MB
    extensions: [
      "js",
      "jsx",
      "ts",
      "tsx",
      "py",
      "java",
      "cpp",
      "c",
      "h",
      "cs",
      "rb",
      "go",
      "php",
      "swift",
      "kt",
      "rs",
      "r",
      "m",
      "sql",
      "sh",
      "bash",
      "json",
      "xml",
      "yaml",
      "yml",
    ],
    mimeTypes: [
      "text/javascript",
      "application/javascript",
      "text/x-python",
      "text/x-java-source",
      "text/x-c",
      "text/x-c++",
      "text/plain",
    ],
  },
  dataset: {
    maxSize: 100 * 1024 * 1024, // 100MB
    extensions: ["csv", "json", "xlsx", "hdf5", "h5", "mat", "parquet"],
    mimeTypes: [
      "text/csv",
      "application/json",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ],
  },
  video: {
    maxSize: 500 * 1024 * 1024, // 500MB
    extensions: ["mp4", "mov", "avi", "mkv", "webm", "flv", "wmv"],
    mimeTypes: [
      "video/mp4",
      "video/quicktime",
      "video/x-msvideo",
      "video/x-matroska",
      "video/webm",
    ],
  },
  audio: {
    maxSize: 50 * 1024 * 1024, // 50MB
    extensions: ["mp3", "wav", "ogg", "m4a", "flac", "aac"],
    mimeTypes: [
      "audio/mpeg",
      "audio/wav",
      "audio/ogg",
      "audio/mp4",
      "audio/flac",
      "audio/aac",
    ],
  },
  archive: {
    maxSize: 100 * 1024 * 1024, // 100MB
    extensions: ["zip", "rar", "7z", "tar", "gz", "bz2"],
    mimeTypes: [
      "application/zip",
      "application/x-rar-compressed",
      "application/x-7z-compressed",
      "application/x-tar",
      "application/gzip",
    ],
  },
  other: {
    maxSize: 50 * 1024 * 1024, // 50MB
    extensions: [],
    mimeTypes: [],
  },
};

/**
 * Get file extension from filename
 */
export function getFileExtension(filename) {
  if (!filename) return "";
  const parts = filename.split(".");
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
}

/**
 * Detect file type from extension
 */
export function detectFileType(filename) {
  const extension = getFileExtension(filename);

  for (const [type, config] of Object.entries(FILE_LIMITS)) {
    if (config.extensions.includes(extension)) {
      return type;
    }
  }

  return "other";
}

/**
 * Format file size to human-readable format
 */
export function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return "0 B";

  const units = ["B", "KB", "MB", "GB", "TB"];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${units[i]}`;
}

/**
 * Validate file before upload
 */
export function validateFileBeforeUpload(file) {
  if (!file) {
    return { valid: false, error: "No file provided" };
  }

  const extension = getFileExtension(file.name);
  if (!extension) {
    return { valid: false, error: "File must have an extension" };
  }

  // Find file type
  let fileType = null;
  for (const [type, config] of Object.entries(FILE_LIMITS)) {
    if (config.extensions.includes(extension)) {
      fileType = type;
      break;
    }
  }

  if (!fileType) {
    fileType = "other";
  }

  // Check size
  const limit = FILE_LIMITS[fileType];
  if (file.size > limit.maxSize) {
    const maxMB = (limit.maxSize / (1024 * 1024)).toFixed(0);
    return {
      valid: false,
      error: `File too large. Max ${maxMB}MB for ${fileType} files`,
      fileType,
    };
  }

  return { valid: true, fileType, extension };
}

/**
 * Get file icon based on file type
 */
export function getFileIcon(fileType) {
  const icons = {
    image: "🖼️",
    pdf: "📄",
    document: "📝",
    presentation: "📊",
    spreadsheet: "📊",
    code: "💻",
    dataset: "📊",
    video: "🎥",
    audio: "🎵",
    archive: "📦",
    other: "📎",
  };
  return icons[fileType] || "📎";
}

/**
 * Get file category from file type (for API)
 */
export function getFileCategory(fileType) {
  const categoryMap = {
    image: "images",
    pdf: "documents",
    document: "documents",
    presentation: "presentations",
    spreadsheet: "data",
    code: "code",
    dataset: "data",
    video: "other",
    audio: "other",
    archive: "other",
    other: "other",
  };
  return categoryMap[fileType] || "other";
}

/**
 * Get MIME type from file extension
 */
export function getMimeType(filename) {
  const extension = getFileExtension(filename);

  for (const config of Object.values(FILE_LIMITS)) {
    const index = config.extensions.indexOf(extension);
    if (index !== -1 && config.mimeTypes[index]) {
      return config.mimeTypes[index];
    }
  }

  return "application/octet-stream";
}

/**
 * Check if file is an image
 */
export function isImageFile(fileType) {
  return fileType === "image";
}

/**
 * Check if file is a document
 */
export function isDocumentFile(fileType) {
  return ["pdf", "document", "presentation", "spreadsheet"].includes(fileType);
}

/**
 * Check if file is code
 */
export function isCodeFile(fileType) {
  return fileType === "code";
}

/**
 * Get file type display name
 */
export function getFileTypeDisplayName(fileType) {
  const displayNames = {
    image: "Image",
    pdf: "PDF",
    document: "Document",
    presentation: "Presentation",
    spreadsheet: "Spreadsheet",
    code: "Code",
    dataset: "Dataset",
    video: "Video",
    audio: "Audio",
    archive: "Archive",
    other: "File",
  };
  return displayNames[fileType] || "File";
}

/**
 * Validate multiple files
 */
export function validateMultipleFiles(files) {
  const results = [];
  const validFiles = [];
  const errors = [];

  for (const file of files) {
    const validation = validateFileBeforeUpload(file);
    results.push({ file, validation });

    if (validation.valid) {
      validFiles.push(file);
    } else {
      errors.push({ file: file.name, error: validation.error });
    }
  }

  return {
    results,
    validFiles,
    errors,
    allValid: errors.length === 0,
  };
}

/**
 * Format time ago
 */
export function timeAgo(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  const intervals = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60,
  };

  for (const [unit, secondsInUnit] of Object.entries(intervals)) {
    const interval = Math.floor(seconds / secondsInUnit);
    if (interval >= 1) {
      return `${interval} ${unit}${interval > 1 ? "s" : ""} ago`;
    }
  }

  return "just now";
}

